/**
 * Client-side rate limiting.
 *
 * The failure this exists to prevent is specific and has a price tag: a
 * component that errors on every render fires an error event on every
 * render, and log ingestion is billed by volume. Left unguarded, the worst
 * bug on the site is also the most expensive one, and the bill arrives
 * before anyone notices the loop.
 *
 * Two independent guards, because they catch different shapes of the same
 * problem:
 *
 * - A **token bucket per event name** bounds sustained volume while still
 *   allowing a legitimate burst — a visitor clicking through six pages in
 *   ten seconds is real traffic, not abuse.
 * - **Consecutive-duplicate suppression** catches the render loop directly.
 *   A bucket alone would still let a loop emit at the refill rate forever;
 *   collapsing identical repeats stops it at one.
 *
 * Nothing is emitted to report a drop. The `event_seq` gap already tells a
 * dashboard that events went missing, and an event announcing suppressed
 * events would reintroduce the volume this module exists to remove.
 *
 * @module logging/pipeline/rate-limiter
 */

import { diagnostic } from '../core/diagnostic';

/** How long an identical repeat is treated as the same occurrence. Long
 *  enough to flatten a render loop, short enough that a visitor genuinely
 *  retrying a failing form is still recorded. */
const DUPLICATE_WINDOW_MS = 10_000;

const MS_PER_MINUTE = 60_000;

export interface RateLimiterOptions {
  /** Events of one name allowed back-to-back before the bucket empties. */
  readonly burst: number;
  /** Sustained rate, per event name, once the burst is spent. */
  readonly perMinute: number;
}

export interface RateLimiter {
  /**
   * Whether this event may be emitted.
   *
   * `signature` identifies "the same thing happening again" — for errors,
   * the category and message. Omit it for events where repetition is
   * meaningful (two page views of the same path are two real views).
   */
  allow(eventName: string, signature?: string): boolean;
  /** Events suppressed so far. Diagnostics only; never emitted. */
  readonly dropped: () => number;
}

interface Bucket {
  tokens: number;
  lastRefillAt: number;
}

export function createRateLimiter(options: RateLimiterOptions): RateLimiter {
  const buckets = new Map<string, Bucket>();
  const lastSeen = new Map<string, number>();
  let dropped = 0;

  function takeToken(eventName: string, now: number): boolean {
    const bucket = buckets.get(eventName) ?? {
      tokens: options.burst,
      lastRefillAt: now,
    };

    // Continuous refill rather than a windowed reset: a window boundary
    // lets a caller spend the full burst twice in quick succession by
    // straddling it.
    const elapsed = now - bucket.lastRefillAt;
    const refilled = (elapsed / MS_PER_MINUTE) * options.perMinute;
    bucket.tokens = Math.min(options.burst, bucket.tokens + refilled);
    bucket.lastRefillAt = now;

    const permitted = bucket.tokens >= 1;
    if (permitted) bucket.tokens -= 1;

    buckets.set(eventName, bucket);
    return permitted;
  }

  return {
    allow(eventName: string, signature?: string): boolean {
      const now = Date.now();

      if (signature !== undefined) {
        const key = `${eventName}:${signature}`;
        const seenAt = lastSeen.get(key);
        if (seenAt !== undefined && now - seenAt < DUPLICATE_WINDOW_MS) {
          dropped += 1;
          return false;
        }
        lastSeen.set(key, now);

        // The map is keyed by message text, so an error loop that varies
        // its message would otherwise grow it without bound. Clearing
        // wholesale is fine: the worst case is that one repeat gets through
        // a moment early.
        if (lastSeen.size > 64) lastSeen.clear();
      }

      if (!takeToken(eventName, now)) {
        dropped += 1;
        diagnostic(`rate limited: ${eventName}`);
        return false;
      }

      return true;
    },

    dropped: () => dropped,
  };
}
